import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useJobsite } from "../context/JobsiteContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/app/Common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { fmtDate } from "../lib/utils";
import { ScrollText, ImagePlus, X } from "lucide-react";

const PAGE = 10;

export default function DailyLogs() {
  const { activeSite } = useJobsite();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sentinelRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (busy || done) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/logs?skip=${logs.length}&limit=${PAGE}${activeSite ? `&jobsite_id=${activeSite.id}` : ""}`);
      setLogs((p) => {
        const seen = new Set(p.map((x) => x.id));
        const fresh = data.filter((x) => !seen.has(x.id));
        return [...p, ...fresh];
      });
      if (data.length < PAGE) setDone(true);
    } finally { setBusy(false); }
  }, [logs.length, busy, done, activeSite]);

  useEffect(() => {
    setLogs([]); setDone(false);
    (async () => {
      const { data } = await api.get(`/logs?skip=0&limit=${PAGE}${activeSite ? `&jobsite_id=${activeSite.id}` : ""}`);
      setLogs(data);
      if (data.length < PAGE) setDone(true);
    })();
  }, [activeSite]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    const encoded = await Promise.all(files.map(fileToCompressedDataUrl));
    setImages((p) => [...p, ...encoded].slice(0, 5));
    e.target.value = "";
  };

  const submit = async () => {
    if (!activeSite) { toast.error("Select a jobsite"); return; }
    if (!notes.trim()) return;
    try {
      const { data } = await api.post("/logs", { jobsite_id: activeSite.id, notes, images });
      setLogs((p) => [data, ...p]);
      setNotes(""); setImages([]);
      toast.success("Log posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <PageHeader
        title="Daily Logs"
        subtitle={activeSite ? `Notes & photos for ${activeSite.name}. Infinite scroll history below.` : "Pick a jobsite to log."}
      />

      {/* Composer */}
      <div className="border border-border bg-card rounded-sm p-5 mb-8" data-testid="log-composer">
        <div className="overline mb-2">New log entry</div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What happened on site today?"
          className="rounded-sm font-mono text-sm min-h-[100px]"
          data-testid="log-notes-input"
        />
        {images.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="log-image-previews">
            {images.map((src, i) => (
              <div key={i} className="relative">
                <img src={src} alt="preview" className="h-16 w-16 object-cover rounded-sm border border-border" />
                <button
                  type="button"
                  onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                  className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-destructive-foreground rounded-full grid place-items-center"
                  data-testid={`log-remove-image-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-border text-xs font-mono uppercase tracking-widest cursor-pointer hover:bg-accent">
            <ImagePlus className="h-3.5 w-3.5" /> Add image
            <input type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid="log-image-input" />
          </label>
          <div className="flex-1" />
          <Button onClick={submit} disabled={!notes.trim() || !activeSite} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="log-submit-btn">Post log</Button>
        </div>
      </div>

      {logs.length === 0 && done ? (
        <EmptyState title="No logs yet" body="Be the first to post today's activity." />
      ) : (
        <div className="space-y-3" data-testid="logs-list">
          {logs.map((l) => (
            <div key={l.id} className="border border-border bg-card rounded-sm p-5" data-testid={`log-row-${l.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <ScrollText className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{l.user_name}</span>
                <span className="text-[11px] text-muted-foreground font-mono ml-auto">{fmtDate(l.created_at)}</span>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{l.notes}</div>
              {l.images?.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {l.images.map((src, i) => (
                    <img key={i} src={src} alt="log" className="h-24 w-24 object-cover rounded-sm border border-border" />
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={sentinelRef} className="h-8" />
          {!done && <div className="text-center text-xs font-mono text-muted-foreground">Loading…</div>}
        </div>
      )}
    </div>
  );
}

async function fileToCompressedDataUrl(file) {
  const bmp = await createImageBitmap(file);
  const max = 1200;
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.7);
}